import DashboardWrapper from '../../layout/DashboardWrapper';
import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueries } from '@tanstack/react-query';
import { useToken } from '../../hooks/useToken';
import { api } from '../../lib/api';
import { queryKeys } from '../../lib/queryKeys';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import 'react-circular-progressbar/dist/styles.css';
import { useUser } from '@clerk/clerk-react';
import PathwaySkeleton from '../../components/skeletons/PathwaySkeleton';
import CohortSkeleton from '../../components/skeletons/CohortSkeleton';
import { DataNotFoundSection } from '../../components/sections/DataNotFoundSection';
import CohortCard from '../../components/cards/CohortCard';
import { usePagination } from '../../hooks/usePagination';
import PathwayCard from '../../components/cards/PathwayCard';
import { PaginationControls } from '../../components/sections/PaginationControls';
import { useNavigate, useSearch } from '@tanstack/react-router';

const TrackProgress = () => {
    // userPathways derived from queries — compute with useMemo to avoid update loops
    const [cohortData, setCohortData] = useState([]);
    const [pathwaysLoading, setPathwaysLoading] = useState(true);
    const [cohortsLoading, setCohortsLoading] = useState(true);
    const { user } = useUser();
    const token = useToken();
    const navigate = useNavigate({ from: '/track-progress' });
    const { tab } = useSearch({ from: '/track-progress' });

    const handleTabChange = newTab => {
        navigate({ search: prev => ({ ...prev, tab: newTab }) });
    };

    useEffect(() => {
        document.title = 'Track Progress | Zindua LMS';
    }, []);

    const cohortQuery = useQuery({
        queryKey: queryKeys.userCohortEnrollments(user?.id),
        queryFn: () => api.getUserCohortEnrollments(user.id, token),
        enabled: !!token && !!user?.id,
        select: data => data.enrolments || [],
    });

    useEffect(() => {
        setCohortData(cohortQuery.data || []);
        setCohortsLoading(cohortQuery.isLoading);
    }, [cohortQuery.data, cohortQuery.isLoading]);

    // pathway details (progress + courses) will be fetched using useQueries below

    const pathwaysQuery = useQuery({
        queryKey: queryKeys.userPathways(),
        queryFn: () => api.getUserPathwayEnrollments(token),
        enabled: !!token,
        select: data => data.pathways || [],
    });

    useEffect(() => {
        setPathwaysLoading(pathwaysQuery.isLoading);
    }, [pathwaysQuery.isLoading]);

    // For each pathway, fetch progress and courses in parallel using useQueries
    const pathwayProgressQueries = useQueries({
        queries: (pathwaysQuery.data || []).map(pathway => ({
            queryKey: queryKeys.pathwayProgress(user?.id, pathway.id),
            queryFn: () => api.getPathwayProgress(user.id, pathway.id, token),
            enabled: !!token && !!user?.id && !!pathwaysQuery.data,
            staleTime: 5 * 60 * 1000,
        })),
    });

    const pathwayCoursesQueries = useQueries({
        queries: (pathwaysQuery.data || []).map(pathway => ({
            queryKey: queryKeys.pathwayCourses(pathway.id),
            queryFn: () => api.getPathwayCourses(pathway.id, token),
            enabled: !!token && !!pathwaysQuery.data,
            staleTime: 5 * 60 * 1000,
        })),
    });

    const userPathways = useMemo(() => {
        const paths = pathwaysQuery.data || [];
        if (!paths.length) return [];

        return paths.map((p, idx) => ({
            ...p,
            progress: pathwayProgressQueries[idx]?.data?.progress?.progress || 0,
            associatedCourses: pathwayCoursesQueries[idx]?.data?.courses || [],
        }));
    }, [pathwaysQuery.data, pathwayProgressQueries, pathwayCoursesQueries]);

    const cohortPagination = usePagination(cohortData, 5);
    return (
        <DashboardWrapper>
            <div className="w-full">
                <div className="flex flex-col gap-2 py-5">
                    <h1 className="capitalize">Track My Progress</h1>
                    <p className="text-neutral-700">
                        Monitor your learning milestones, review completed activities, and stay aligned with your goals.
                    </p>
                </div>
                <Tabs
                    value={tab === 'cohort' ? 'cohort' : 'pathways'}
                    onValueChange={handleTabChange}
                    className="w-full max-w-250"
                >
                    <div className="max-md:flex flex-row justify-center items-center w-full">
                        <TabsList className="justify-center max-md:w-full">
                            <TabsTrigger value="pathways" className="justify-center max-md:w-full capitalize">
                                My Pathways
                            </TabsTrigger>
                            <TabsTrigger value="cohort" className="justify-center max-md:w-full capitalize">
                                Cohort Analytics
                            </TabsTrigger>
                        </TabsList>
                    </div>
                    <TabsContent value="pathways">
                        {pathwaysLoading ? (
                            <PathwaySkeleton />
                        ) : userPathways.length > 0 ? (
                            <div className="w-full">
                                <div className="flex w-full grow flex-col gap-4">
                                    {userPathways.map(pathway => (
                                        <PathwayCard pathway={pathway} key={pathway.id} />
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <DataNotFoundSection title="No Pathways Found" />
                        )}
                    </TabsContent>
                    <TabsContent value="cohort">
                        {cohortsLoading ? (
                            <CohortSkeleton />
                        ) : cohortData.length === 0 ? (
                            <DataNotFoundSection title="No cohort enrollments found" />
                        ) : (
                            <div className="w-full">
                                <div className="grid grid-cols-1 md:grid-col-2 lg:grid-cols-3 gap-4 mt-4">
                                    {cohortPagination.currentData.map((cohort, idx) => (
                                        <CohortCard key={`cohort-${cohort.cohort_id || cohort.id || idx}`} cohort={cohort} />
                                    ))}
                                </div>
                                {cohortPagination.totalPages > 1 && (
                                    <PaginationControls
                                        pagination={cohortPagination}
                                        totalItems={cohortPagination.totalItems}
                                        itemType="cohorts"
                                    />
                                )}
                            </div>
                        )}
                    </TabsContent>
                </Tabs>
            </div>
        </DashboardWrapper>
    );
};
export default TrackProgress;
